import { Observable, throwError, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HelperService } from './helper.service';
import { Router } from '@angular/router';
import { OverlayService } from './overlayService';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { IS_UNCANCELABLE_REQUEST } from '../models';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WarningLoggedoutModalComponent } from '../components/warning-loggedout-modal/warning-loggedout-modal.component';
import { TranslatePipe } from '../pipes/translate.pipe';
import { CommonLogMessages, LogService } from './log.service';
import { HttpHelperService } from './http-helper.service';
import { HealthCheckService } from './health-check.service';
import { isPermissionDenied401, isTransientAuthFailure } from '../interceptors/http.interceptor';
import { clearBrowserStorageKeepingPreferences } from '../utils/browser-storage.utils';
import { showContentUnavailable } from '../utils/content-unavailable.utils';

export type ErrorHandlerOptions = {
  redirectOnForbidden?: boolean;
};

export type ApiRequestOptions = {
  blobResponse?: boolean;
  forbiddenTest?: boolean;
  redirectOnForbidden?: boolean;

  //for newPost, newPut
  uploadFile?: boolean;
  headers?: any;
  uncancelable?: boolean;
  responseType?: string;
  skipErrorHandling?: boolean;
};

export type HandleErrorParams = {
  error: any;
  options?: ErrorHandlerOptions;
  url: string;
  method?: string;
  body?: any;
};

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  baseUrl: string;
  LOGOUT_SESSION_EXPIRY_KEY = 'loggedOutDueToSessionExpiryMessage';
  private isLogoutModalShown = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly activeModal: NgbActiveModal,
    private readonly overlayService: OverlayService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly translatePipe: TranslatePipe,
    private readonly logService: LogService,
    private readonly httpHelperService: HttpHelperService,
    private readonly helperService: HelperService,
    private readonly healthCheckService: HealthCheckService
  ) {
    this.baseUrl = environment.api.baseUrl;
  }

  private processResponse(response: any): any {
    return response == null ? null : response || {};
  }

  private getErrorHandlerOptions(request: ApiRequestOptions) {
    const errorHandlerOptions: ErrorHandlerOptions = {};

    if (request?.redirectOnForbidden) {
      errorHandlerOptions.redirectOnForbidden = request.redirectOnForbidden;
    }

    return errorHandlerOptions;
  }

  httpGet(
    res: string,
    analyticsApi: boolean = false,
    customHeaders: any = {},
    options: ApiRequestOptions = {}
  ): Observable<any> {
    const headers: HttpHeaders = this.httpHelperService.getHttpHeaders(false, customHeaders);
    const baseUrl = analyticsApi ? environment.api.analyticsUrl : this.baseUrl;
    const endpointUrl = baseUrl + res;

    const getOptions: any = { headers, withCredentials: true };

    if (options.blobResponse) {
      getOptions.responseType = 'blob' as 'json';
    } else {
      getOptions.responseType = 'json';
    }

    const errorHandlerOptions = this.getErrorHandlerOptions(options);

    return this.httpClient.get<any>(endpointUrl, getOptions).pipe(
      map((data: any) => {
        if (options.blobResponse) {
          return data;
        } else {
          return this.processResponse(data);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (res.endsWith('api/dashboards') || res.endsWith('api/selfTrainer')) {
          return of({ menus: [] });
        }
        if (options?.skipErrorHandling) {
          return throwError(() => error);
        }
        return this.handleError({
          error,
          options: errorHandlerOptions,
          url: endpointUrl,
          method: 'GET',
        });
      })
    );
  }

  /**
   * HTTP Post
   * @param res
   * @param dataPost
   * @param uploadFile
   * @param cancellable
   * @param customHeaders
   * @param options
   * @returns {Observable<R>}
   */
  analyticsHttpPost(
    res: string,
    dataPost: any,
    uploadFile: boolean = false,
    cancellable: boolean = true,
    customHeaders?: any,
    options?: ApiRequestOptions
  ): Observable<any> {
    const headers = this.httpHelperService.getHttpHeaders(uploadFile, customHeaders);
    const json = typeof dataPost !== 'string' ? JSON.stringify(dataPost) : dataPost;
    const endpointUrl = environment.api.analyticsUrl + res;

    const errorHandlerOptions = this.getErrorHandlerOptions(options);

    return this.httpClient
      .post<any>(endpointUrl, json, {
        headers,
        withCredentials: true,
        responseType: 'json',
      })
      .pipe(
        map((response: any) => this.processResponse(response)),
        catchError((error) => {
          if (options?.skipErrorHandling) {
            return throwError(() => error);
          }
          return this.handleError({
            error,
            options: errorHandlerOptions,
            url: endpointUrl,
            body: json,
            method: 'POST',
          });
        })
      );
  }

  httpPost(
    res: string,
    dataPost: any,
    uploadFile: boolean = false,
    cancellable: boolean = true,
    customHeaders?: any,
    options?: ApiRequestOptions
  ): Observable<any> {
    const headers = this.httpHelperService.getHttpHeaders(uploadFile, customHeaders);
    const json = typeof dataPost !== 'string' ? JSON.stringify(dataPost) : dataPost;

    const endpointUrl = this.baseUrl + res;
    const errorHandlerOptions = this.getErrorHandlerOptions(options);

    return this.httpClient
      .post<any>(endpointUrl, json, {
        headers,
        withCredentials: true,
        responseType: 'json',
      })
      .pipe(
        map((response: any) => this.processResponse(response)),
        catchError((error) => {
          if (options?.skipErrorHandling) {
            return throwError(() => error);
          }
          return this.handleError({
            error,
            options: errorHandlerOptions,
            url: endpointUrl,
            body: json,
            method: 'POST',
          });
        })
      );
  }

  httpPostWithoutBody(res: string, uploadFile: boolean = false, cancellable: boolean = true): Observable<any> {
    const headers = this.httpHelperService.getHttpHeaders(uploadFile);
    const endpointUrl = this.baseUrl + res;

    return this.httpClient
      .post<any>(endpointUrl, null, {
        headers,
        withCredentials: true,
        responseType: 'json',
      })
      .pipe(
        map((response: any) => this.processResponse(response)),
        catchError((error) => {
          return this.handleError({
            error,
            options: {},
            method: 'POST',
            url: endpointUrl,
          });
        })
      );
  }

  newPost(endpoint: string, payload: any, options: ApiRequestOptions): Observable<any> {
    const json = typeof payload !== 'string' ? JSON.stringify(payload) : payload;
    const headers: Record<string, string> = {};
    const httpHeaders = this.httpHelperService.getHttpHeaders(options?.uploadFile || false);

    httpHeaders.keys().forEach((name) => {
      const value = httpHeaders.get(name);
      if (value) {
        headers[name] = value;
      }
    });

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    const postOptions: any = { headers, withCredentials: true };

    if (options.uncancelable) {
      postOptions.context = new HttpContext().set(IS_UNCANCELABLE_REQUEST, true);
    }

    if (options.responseType) {
      postOptions.responseType = options.responseType;
    }

    const errorHandlerOptions = this.getErrorHandlerOptions(options);

    return this.httpClient.post(this.baseUrl + endpoint, json, postOptions).pipe(
      //map(response => response.json()),
      catchError((error) => {
        if (options?.skipErrorHandling) {
          return throwError(() => error);
        }
        return this.handleError({
          error,
          options: errorHandlerOptions,
          method: 'POST',
          url: this.baseUrl + endpoint,
          body: json,
        });
      })
    );
  }

  httpPut(res: string, dataPost: any, options: ApiRequestOptions = {}): Observable<any> {
    const headers = this.httpHelperService.getHttpHeaders(false);
    const json = typeof dataPost !== 'string' ? JSON.stringify(dataPost) : dataPost;

    const endpointUrl = this.baseUrl + res;
    const errorHandlerOptions = this.getErrorHandlerOptions(options);

    return this.httpClient
      .put<any>(endpointUrl, json, {
        headers,
        withCredentials: true,
        responseType: 'json',
      })
      .pipe(
        map((response: any) => this.processResponse(response)),
        catchError((error) => {
          if (options?.skipErrorHandling) {
            return throwError(() => error);
          }
          return this.handleError({
            error,
            options: errorHandlerOptions,
            method: 'PUT',
            url: endpointUrl,
            body: json,
          });
        })
      );
  }

  newPut(endpoint: string, payload: any, options: ApiRequestOptions): Observable<any> {
    const json = typeof payload !== 'string' ? JSON.stringify(payload) : payload;
    const headers: Record<string, string> = {};
    const httpHeaders = this.httpHelperService.getHttpHeaders(options?.uploadFile || false);

    httpHeaders.keys().forEach((name) => {
      const value = httpHeaders.get(name);
      if (value) {
        headers[name] = value;
      }
    });

    const putOptions: any = { headers, withCredentials: true };
    if (options.uncancelable) {
      putOptions.context = new HttpContext().set(IS_UNCANCELABLE_REQUEST, true);
    }

    const errorHandlerOptions = this.getErrorHandlerOptions(options);

    return this.httpClient.put(this.baseUrl + endpoint, json, putOptions).pipe(
      catchError((error) => {
        if (options?.skipErrorHandling) {
          return throwError(() => error);
        }
        return this.handleError({
          error,
          options: errorHandlerOptions,
          url: this.baseUrl + endpoint,
          body: json,
          method: 'PUT',
        });
      })
    );
  }

  httpDelete(res: string, body: any = null, apiOptions: ApiRequestOptions = {}): Observable<any> {
    const headers = this.httpHelperService.getHttpHeaders(false);
    const endpointUrl = this.baseUrl + res;

    const options: any = {
      headers,
      withCredentials: true,
      responseType: 'json',
    };

    if (body != null) {
      options.body = typeof body !== 'string' ? JSON.stringify(body) : body;
    }

    const errorHandlerOptions = this.getErrorHandlerOptions(apiOptions);

    return this.httpClient.delete<any>(endpointUrl, options).pipe(
      map((response: any) => this.processResponse(response)),
      catchError((error) => {
        if (apiOptions?.skipErrorHandling) {
          return throwError(() => error);
        }
        return this.handleError({
          error,
          options: errorHandlerOptions,
          url: endpointUrl,
          method: 'DELETE',
        });
      })
    );
  }

  /**
   * Check if the platform is up and redirect to maintenance page if not.
   * The session is kept: an unhealthy backend is not a reason to log the user out.
   */
  public healthCheck(): void {
    if (!navigator.onLine) {
      this.router.navigate(['entry', 'offline'], { skipLocationChange: true });
      return;
    }

    this.healthCheckService.probe().subscribe((healthy) => {
      if (!healthy) {
        this.router.navigate(['entry', 'maintenance'], { skipLocationChange: true });
      }
    });
  }

  /**
   * Handle error
   * @param params
   * @returns {any}
   */
  private handleError(params: HandleErrorParams): Observable<any> {
    const { error, options = {}, url, body = null } = params;

    // HttpClient wraps errors in HttpErrorResponse.
    // A 401 that survived a successful token renewal is a permission refusal: handle it as a 403.
    const status = isPermissionDenied401(error) ? 403 : error?.status;
    const errorBody = error?.error; // already parsed if JSON, raw text otherwise
    const errorCode = errorBody?.errorCode;

    if (isTransientAuthFailure(error)) {
      // 401 whose app-token renewal could not complete (IAM unreachable, 429, 5xx): keep the session.
      return throwError(() => errorBody || error);
    }

    if (status === 401) {
      if (errorCode) {
        if (errorCode === 'NOT_PROJECT_MEMBER') {
          this.overlayService.showError(`You don't have access to view this page`, 'Access Denied');
          return throwError(() => errorBody || error);
        } else if (errorCode === 'ADMIN_ACCESS_ONLY_ACCESS_DENIED') {
          return throwError(() => errorBody || error);
        } else {
          this.logService.logMessage('warning', CommonLogMessages.AutoLogout, {
            cause: '401',
            error,
            url,
            body,
          });

          return this.httpClient
            .post(this.baseUrl + '/api/auth/logout', null, {
              withCredentials: true,
              responseType: 'json',
            })
            .pipe(
              map((resp: any) => this.processResponse(resp)),
              finalize(() => {
                clearBrowserStorageKeepingPreferences();
                this.logService.setUser(null);
                this.router.navigateByUrl('/entry').then(() => {
                  this.activeModal.close();
                  if (!this.isLogoutModalShown) {
                    this.isLogoutModalShown = true;
                    const modalRef = this.modalService.open(WarningLoggedoutModalComponent);
                    modalRef.componentInstance.logoutMessageKey = this.LOGOUT_SESSION_EXPIRY_KEY;
                    modalRef.closed.subscribe(() => {
                      this.isLogoutModalShown = false;
                    });
                  }
                });
              })
            );
        }
      } else {
        // 401 without structured JSON body
        this.logService.logMessage('warning', CommonLogMessages.AutoLogout, {
          cause: '401',
          error,
          url,
          body,
        });

        return this.httpClient
          .post(this.baseUrl + '/api/auth/logout', null, {
            withCredentials: true,
            responseType: 'json',
          })
          .pipe(
            map((resp: any) => this.processResponse(resp)),
            finalize(() => {
              clearBrowserStorageKeepingPreferences();
              this.logService.setUser(null);
              this.router.navigateByUrl('/entry').then(() => {
                this.activeModal.close();
                if (!this.isLogoutModalShown) {
                  this.isLogoutModalShown = true;
                  const modalRef = this.modalService.open(WarningLoggedoutModalComponent);
                  modalRef.componentInstance.logoutMessageKey = this.LOGOUT_SESSION_EXPIRY_KEY;
                  modalRef.closed.subscribe(() => {
                    this.isLogoutModalShown = false;
                  });
                }
              });
            })
          );
      }
    } else if (status === 403) {
      if (options.redirectOnForbidden) {
        const rootUrls = ['/workspaces', '/projects', '/workspaces/projects'];
        if (rootUrls.includes(window.location.pathname)) {
          this.overlayService.showError(this.translatePipe.transform('overlayErrorNoAccessPermission'));
        } else {
          showContentUnavailable(this.router);
        }
      }
      return throwError(() => errorBody || error);
    } else if (status === 410) {
      // Item no longer exists — preserve the HttpErrorResponse so callers can detect the 410 status.
      return throwError(() => error);
    } else {
      return throwError(() => errorBody || error);
    }
  }

  /**
   * Retrieve item's files
   *
   * @param projectId
   * @param sectionId
   * @param subsectionId
   * @param itemId
   */
  public getItemFiles(projectId: string, sectionId: string, subsectionId: string, itemId: string): Observable<any> {
    return this.httpGet(
      `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/items/${itemId}/files`
    );
  }

  /**
   * Delete item file
   *
   * @param projectId
   * @param sectionId
   * @param subsectionId
   * @param itemId
   * @param fileId
   */
  public deleteItemFile(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    fileId: string
  ): Observable<any> {
    return this.httpDelete(
      `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/items/${itemId}/files/${fileId}`
    );
  }

  /**
   * Sort files list
   *
   * @param projectId
   * @param sectionId
   * @param subsectionId
   * @param itemId
   * @param fileList
   */
  public updateFilesOrder(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    fileList: any[]
  ): Observable<any> {
    return this.httpPut(
      `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/items/${itemId}/files`,
      fileList
    );
  }

  public setProfileAndImagesProbForItems(url): Observable<any> {
    return new Observable((observe) => {
      this.httpGet(url).subscribe(
        (data) => {
          if (!data || !Array.isArray(data)) {
            observe.next(data);
            observe.complete();
          }

          this.setProfileAndImagesProb(data);

          observe.next(data);
          observe.complete();
        },
        (err) => observe.error(err)
      );
    });
  }

  private setProfileAndImagesProb(items) {
    items.map((s: any) => {
      const elementFiles = s.files;
      s.items = elementFiles;

      if (s.urls) s.items = s.items.concat(s.urls);

      s.updatePreviewImage = () => {};

      // setProfileAndImagesProb(s, elementFiles);
    });
  }

  /**
   *
   * @param projectId
   * @param sectionId
   * @param subsectionId
   * @param itemId
   * @param fileId
   * @param displayName
   * @returns
   */
  public updateDisplayName(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    fileId: string,
    displayName: any
  ): Observable<any> {
    return this.httpPut(
      `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/items/${itemId}/files/${fileId}/updateDisplayName?displayName=${displayName}`,
      displayName
    );
  }
}
export class UrlSanitizer {
  static sanitizeUrl(url: string): string {
    // Define the regular expression pattern to match characters not allowed in URLs
    const pattern = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/g;

    // Use replace method to remove invalid characters from the URL
    return url.replace(pattern, '');
  }
}
